import {
    CheckIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    PencilSquareIcon,
    TrashIcon,
} from "@heroicons/react/24/outline";
import CatalogStatusChip, {
    catalogStatusLabel,
    normalizeCatalogStatus,
} from "../productCatalog/CatalogStatusChip";
import SupplySku from "../SupplySku";

const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const currencyFormatterWithPrecision = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
});

function formatMoney(value) {
    return currencyFormatter.format(Number(value || 0));
}

function formatMoneyWithPrecision(value) {
    return currencyFormatterWithPrecision.format(Number(value || 0));
}

function RowCheckbox({ checked, onChange }) {
    return (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                onChange?.();
            }}
            className={`flex h-4 w-4 items-center justify-center rounded border transition-colors ${
                checked ? "border-[#111] bg-[#111]" : "border-gray-300 bg-white hover:border-gray-500"
            }`}
            aria-label={checked ? "Deselect row" : "Select row"}
        >
            {checked ? <CheckIcon className="h-2.5 w-2.5 text-white" /> : null}
        </button>
    );
}

function StatusChip({ status }) {
    const normalizedStatus = normalizeCatalogStatus(status);
    return normalizedStatus ? (
        <CatalogStatusChip status={normalizedStatus} label={catalogStatusLabel(normalizedStatus)} />
    ) : (
        "—"
    );
}

export function BomEmptyState({
    title,
    description,
    actionLabel,
    onAction,
}) {
    return (
        <div className="flex min-h-[320px] flex-col items-center justify-center rounded-xl bg-white px-6 text-center">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="mt-2 max-w-md text-sm text-gray-500">{description}</p>
            {actionLabel ? (
                <button
                    type="button"
                    onClick={onAction}
                    className="mt-5 inline-flex items-center rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                >
                    {actionLabel}
                </button>
            ) : null}
        </div>
    );
}

function formatColumnMoney(column, value) {
    if (column?.key === "totalBomCost") {
        return formatMoneyWithPrecision(value);
    }
    return formatMoney(value);
}

function renderCellValue(column, row, parentRow = null) {
    const rawValue =
        typeof column?.render === "function" ? column.render(row, parentRow) : row?.[column?.key];
    return column?.type === "money" ? formatColumnMoney(column, rawValue) : rawValue;
}

function FragmentRow({
    row,
    columns,
    childColumns,
    childRows,
    fillerChildCellCount,
    isExpanded,
    selectable,
    isSelected,
    onToggleRow,
    onEditRow,
    onDeleteRow,
    onToggleSelect,
}) {
    const firstColumn = columns[0];
    const remainingColumns = columns.slice(1);
    const firstChildColumn = childColumns[0];
    const remainingChildColumns = childColumns.slice(1);
    const packagingCost = Number(
        (childRows || []).reduce((sum, childRow) => {
            const linkedPackagingGroupId =
                childRow?.packagingGroupId != null && String(childRow.packagingGroupId).trim() !== ""
                    ? childRow.packagingGroupId
                    : childRow?.packaging_group_id;
            if (linkedPackagingGroupId == null || String(linkedPackagingGroupId).trim() === "") {
                return sum;
            }
            return sum + (Number(childRow?.qty || 0) || 0) * (Number(childRow?.unitCost || 0) || 0);
        }, 0).toFixed(4)
    );

    return (
        <>
            <tr
                className={`group border-b border-gray-200 cursor-pointer select-none transition-colors ${
                    isSelected
                        ? "bg-blue-50/40 border-b-blue-100"
                        : isExpanded
                          ? "bg-white border-b-gray-100"
                          : "bg-white hover:bg-gray-50/60"
                }`}
                onClick={onToggleRow}
            >
                {selectable ? (
                    <td
                        className="sticky left-0 z-10 bg-inherit w-[36px] min-w-[36px] pl-3"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <RowCheckbox checked={isSelected} onChange={onToggleSelect} />
                    </td>
                ) : null}
                <td
                    className={`sticky ${selectable ? "left-[36px]" : "left-0"} z-10 bg-inherit pl-2 pr-3 py-2.5 w-[220px] min-w-[220px]`}
                >
                    <div className="flex items-center gap-2">
                        <span
                            className="flex-shrink-0 text-gray-400 hover:text-gray-700 transition-colors"
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggleRow?.();
                            }}
                        >
                            {childRows.length ? (
                                isExpanded ? (
                                    <ChevronDownIcon className="h-3.5 w-3.5" />
                                ) : (
                                    <ChevronRightIcon className="h-3.5 w-3.5" />
                                )
                            ) : null}
                        </span>
                        <div className="min-w-0">{renderCellValue(firstColumn, row)}</div>
                    </div>
                </td>
                {remainingColumns.map((column) => (
                    <td
                        key={column.key}
                        className={[
                            "px-3 py-2.5 text-sm text-gray-700",
                            column.align === "right" ? "text-right" : "text-left",
                            column.key === "canonicalName" ? "w-[380px] max-w-[380px]" : "",
                            column.additionalClassname ? column.additionalClassname : "",
                        ].join(" ")}
                    >
                        {renderCellValue(column, row)}
                    </td>
                ))}
                <td className="px-2 py-2.5 w-[36px] min-w-[36px]">
                    <div className="flex items-center justify-center gap-1">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onEditRow?.();
                            }}
                            className="flex h-6 w-6 items-center justify-center rounded text-gray-300 opacity-0 transition-all hover:bg-gray-100 hover:text-gray-700 group-hover:opacity-100 focus:opacity-100"
                            title="Edit row"
                        >
                            <PencilSquareIcon className="h-3.5 w-3.5" />
                        </button>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDeleteRow?.();
                            }}
                            className="flex h-6 w-6 items-center justify-center rounded text-gray-300 opacity-0 transition-all hover:bg-gray-100 hover:text-gray-700 group-hover:opacity-100 focus:opacity-100"
                            title="Delete row"
                        >
                            <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </td>
            </tr>
            {isExpanded &&
                childRows.map((childRow, index) => (
                    <tr
                        key={childRow.id || `${row.id}-${index}`}
                        className={`${isSelected ? "bg-blue-50/30" : "bg-[#fafafa]"} hover:bg-gray-50 ${
                            index === childRows.length - 1 ? "" : "border-b border-gray-100/80"
                        }`}
                    >
                        {selectable ? (
                            <td className="sticky left-0 z-10 bg-inherit w-[36px] min-w-[36px] pl-3" />
                        ) : null}
                        <td
                            className={`sticky ${selectable ? "left-[36px]" : "left-0"} z-10 bg-inherit pl-6 pr-3 py-1.5 w-[220px] min-w-[220px]`}
                        >
                            <div className="flex items-center gap-1.5">
                                <span className="h-3.5 w-px bg-gray-300 flex-shrink-0 mt-px" />
                                <div className="min-w-0 text-sm text-gray-700 leading-tight">
                                    {firstChildColumn ? renderCellValue(firstChildColumn, childRow, row) : "—"}
                                </div>
                            </div>
                        </td>
                        {remainingChildColumns.map((column) => {
                            const rawValue =
                                typeof column.render === "function"
                                    ? column.render(childRow, row)
                                    : childRow?.[column.key];
                            return (
                                <td
                                    key={column.key}
                                    className={[
                                        "px-3 py-1.5 text-sm text-gray-600",
                                        column.align === "right" ? "text-right" : "text-left",
                                    ].join(" ")}
                                >
                                    {column.type === "money" ? formatMoneyWithPrecision(rawValue) : rawValue}
                                </td>
                            );
                        })}
                        {Array.from({ length: fillerChildCellCount }).map((_, fillerIndex) => (
                            <td
                                key={`filler-${row.id}-${index}-${fillerIndex}`}
                                className={`px-3 py-1.5 text-xs text-gray-300 ${fillerIndex === fillerChildCellCount - 1 ? "text-left" : "text-right"}`}
                            >
                                —
                            </td>
                        ))}
                        <td className="px-2 py-1.5 w-[36px] min-w-[36px]" />
                    </tr>
                ))}
            {isExpanded ? (
                <tr className={`border-b border-gray-200 ${isSelected ? "bg-blue-50/20" : "bg-gray-50/40"}`}>
                    <td colSpan={columns.length + (selectable ? 2 : 1)} className="px-9 py-1.5">
                        <div className="flex items-center justify-between text-xs text-gray-400">
                            <span>
                                {Number(row.componentCount || childRows.length || 0)} components
                                {row.bomStatus ? (
                                    <>
                                        {" "}
                                        · BOM Status: <span className="font-medium text-gray-500">{row.bomStatus}</span>
                                    </>
                                ) : null}
                            </span>
                            <div className="flex items-center gap-2 flex-row">
                                <span className="font-medium text-gray-600">Total: {formatMoneyWithPrecision(row.totalBomCost)}</span>
                                {packagingCost > 0 ? (
                                    <span className="font-medium text-gray-400">
                                        (+{formatMoney(packagingCost)} packaging)
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    </td>
                </tr>
            ) : null}
        </>
    );
}

const BomTable = ({
    rows,
    columns,
    childColumns = [],
    childKey = "components",
    expandedIds = [],
    selectable = true,
    selectedIds = [],
    onToggleRow,
    onEditRow,
    onDeleteRow,
    onToggleSelect,
    onToggleSelectAll,
    emptyTitle,
    emptyDescription,
    emptyActionLabel,
    onEmptyAction,
}) => {
    const expandedSet = new Set(expandedIds);
    const selectedSet = new Set(selectedIds);
    const allSelected = rows.length > 0 && rows.every((row) => selectedSet.has(row.id));
    const someSelected = rows.some((row) => selectedSet.has(row.id));
    const parentColumns = Array.isArray(columns) ? columns : [];
    const childColumnCount = Array.isArray(childColumns) ? childColumns.length : 0;
    const fillerChildCellCount = Math.max(parentColumns.length - childColumnCount, 0);

    if (!rows.length) {
        return (
            <BomEmptyState
                title={emptyTitle}
                description={emptyDescription}
                actionLabel={emptyActionLabel}
                onAction={onEmptyAction}
            />
        );
    }

    return (
        <div className="relative overflow-auto rounded-lg border border-gray-200 bg-white shadow-sm h-full">
            <table className="w-full border-collapse text-left" style={{ minWidth: "1240px" }}>
                <thead className="sticky top-0 z-10">
                    <tr className="border-b border-gray-200 bg-gray-50 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        {selectable ? (
                            <th className="sticky left-0 z-20 bg-gray-50 pl-3 pr-2 py-2.5 w-[36px] min-w-[36px]">
                                <button
                                    type="button"
                                    onClick={onToggleSelectAll}
                                    className={`flex h-4 w-4 items-center justify-center rounded border transition-colors ${
                                        allSelected
                                            ? "border-[#111] bg-[#111]"
                                            : someSelected
                                              ? "border-[#555] bg-[#555]"
                                              : "border-gray-300 bg-white hover:border-gray-500"
                                    }`}
                                    aria-label="Select all"
                                >
                                    {(allSelected || someSelected) && <CheckIcon className="h-2.5 w-2.5 text-white" />}
                                </button>
                            </th>
                        ) : null}
                        <th
                            className={`sticky ${selectable ? "left-[36px]" : "left-0"} z-20 bg-gray-50 pl-2 pr-3 py-2.5 w-[220px] min-w-[220px]`}
                        >
                            {parentColumns[0]?.label || ""}
                        </th>
                        {parentColumns.slice(1).map((column) => (
                            <th
                                key={column.key}
                                className={[
                                    "px-3 py-2.5 font-semibold",
                                    column.align === "right" ? "text-right" : "text-left",
                                    column.key === "canonicalName" ? "w-[380px] max-w-[380px]" : "",
                                    column.additionalClassname ? column.additionalClassname : "",
                                ].join(" ")}
                            >
                                {column.label}
                            </th>
                        ))}
                        <th className="px-2 py-2.5 w-[36px] min-w-[36px]" />
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const childRows = Array.isArray(row[childKey]) ? row[childKey] : [];
                        const isExpanded = expandedSet.has(row.id);
                        return (
                            <FragmentRow
                                key={row.id}
                                row={row}
                                columns={parentColumns}
                                childColumns={childColumns}
                                childRows={childRows}
                                fillerChildCellCount={fillerChildCellCount}
                                isExpanded={isExpanded}
                                selectable={selectable}
                                isSelected={selectedSet.has(row.id)}
                                onToggleRow={() => onToggleRow?.(row)}
                                onEditRow={() => onEditRow?.(row)}
                                onDeleteRow={() => onDeleteRow?.(row)}
                                onToggleSelect={() => onToggleSelect?.(row)}
                            />
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export const bomColumnRenderers = {
    sku: (value) => <SupplySku sku={value} />,
    status: (value) => <StatusChip status={value} />,
    money: formatMoney,
};

export default BomTable;
